import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import { formatDateTime } from "@/lib/utils";
import { FormField } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { parseApiError } from "@/lib/error-handling";
import { useToast } from "@/lib/ui";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import {
  useAdminCheckoutsList,
  useAdminCheckoutApproveParticipants,
  useAdminCheckoutApproveBoats,
  useAdminCheckoutRefund,
  useAdminParticipantApprove,
  useAdminParticipantReject,
  useAdminParticipantCancel,
  type AdminCheckoutListItem,
} from "@/lib/admin-api";
import {
  findCrewForBoat,
  boatIsPending,
} from "@/lib/boat-utils";
import {
  hasApprovedBalancePayment,
  hasApprovedDepositPayment,
  hasApprovedPaymentCaptured,
} from "@/lib/payment-status";

type Participant = NonNullable<AdminCheckoutListItem["participants"]>[number] & {
  event?:
    | {
        id: string;
        title: string;
        startsAt: string | null;
        pricePoints?: Array<{ id: string; label: string }>;
      }
    | null;
};

/** Resolve which price point was selected for this participant from the checkout (items + event.pricePoints). */
function getPricePointLabel(
  checkout: AdminCheckoutListItem,
  participant: Participant,
): string | null {
  const eventId = participant.event?.id;
  if (!eventId) return null;
  const item = checkout.items?.find(
    (i) =>
      i.eventItem &&
      i.eventItem.eventId === eventId &&
      i.eventItem.firstName === participant.firstName &&
      i.eventItem.lastName === participant.lastName,
  );
  const pricePointId = item?.eventItem?.eventPricePointId;
  if (!pricePointId) return null;
  const label = participant.event?.pricePoints?.find((pp) => pp.id === pricePointId)
    ?.label;
  return label ?? null;
}

function StatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "danger";
      case "CANCELLED":
        return "info";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

function CheckoutStatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "CANCELLED":
        return "info";
      case "PARTIAL":
        return "info";
      case "MIXED":
        return "warning";
      case "COMPLETE":
        return "success";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

const PENDING_CHECKOUTS_PAGE_SIZE = 10;

export function PendingCheckouts() {
  const navigate = useNavigate();
  const { toastRef, showToast } = useToast();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: PENDING_CHECKOUTS_PAGE_SIZE,
    initialSortField: "createdAt",
    initialSortOrder: -1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * PENDING_CHECKOUTS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean> | null>(null);
  const [expandedBoatRows, setExpandedBoatRows] = useState<Record<string, boolean> | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<
    Record<string, string[]>
  >({});
  const [selectedBoats, setSelectedBoats] = useState<
    Record<string, string[]>
  >({});
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [participantIdToReject, setParticipantIdToReject] = useState<
    string | null
  >(null);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState(false);
  const [participantIdToCancel, setParticipantIdToCancel] = useState<
    string | null
  >(null);
  const [approvingParticipantId, setApprovingParticipantId] = useState<
    string | null
  >(null);
  const menuRefs = useRef<Record<string, Menu | null>>({});
  const [refundDialogVisible, setRefundDialogVisible] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [checkoutIdToRefund, setCheckoutIdToRefund] = useState<string | null>(
    null,
  );
  const [loadingCheckoutId, setLoadingCheckoutId] = useState<string | null>(
    null,
  );

  // Filter by pending participants by default
  const filter = useMemo(() => {
    const search = globalFilter.trim();
    if (search !== "") {
      return `participants.status==PENDING;bookingReference=like=${search}`;
    }
    return "participants.status==PENDING";
  }, [globalFilter]);

  const checkoutsQuery = useAdminCheckoutsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filter,
  });
  const checkouts = checkoutsQuery.data?.data ?? [];
  const totalRecords = checkoutsQuery.data?.pagination.total ?? 0;
  const loading = checkoutsQuery.isFetching;

  const approveMutation = useAdminParticipantApprove();
  const bulkApproveMutation = useAdminCheckoutApproveParticipants();
  const bulkApproveBoatsMutation = useAdminCheckoutApproveBoats();
  const rejectMutation = useAdminParticipantReject();
  const cancelMutation = useAdminParticipantCancel();
  const refundMutation = useAdminCheckoutRefund();

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const handleApprove = async (
    participantId: string,
    _checkoutId: string,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (approvingParticipantId) {
      return;
    }

    setApprovingParticipantId(participantId);

    try {
      await approveMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: {},
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant approved",
      });
    } catch (error) {
      const { message } = parseApiError(error);
      setApprovingParticipantId(null);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participant",
      });
    } finally {
      setApprovingParticipantId(null);
    }
  };

  const handleBulkApprove = async (checkoutId: string) => {
    const selected = selectedParticipants[checkoutId] ?? [];
    if (selected.length === 0) return;

    setLoadingCheckoutId(checkoutId);

    try {
      await bulkApproveMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          participantIds: selected,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${selected.length} participant(s) approved`,
      });

      // Clear selection
      setSelectedParticipants((prev) => {
        const next = { ...prev };
        delete next[checkoutId];
        return next;
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participants",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleBulkApproveBoats = async (checkoutId: string) => {
    const selected = selectedBoats[checkoutId] ?? [];
    if (selected.length === 0) return;

    setLoadingCheckoutId(checkoutId);

    try {
      await bulkApproveBoatsMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          boatIds: selected,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${selected.length} boat(s) approved`,
      });

      // Clear selection
      setSelectedBoats((prev) => {
        const next = { ...prev };
        delete next[checkoutId];
        return next;
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve boats",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleApproveAll = async (checkoutId: string) => {
    const checkout = checkouts.find((c) => c.id === checkoutId);
    if (!checkout) return;

    const pendingParticipants = (checkout.participants ?? []).filter(
      (p) => p.status === "PENDING" && !p.boatId,
    );

    if (pendingParticipants.length === 0) return;

    setLoadingCheckoutId(checkoutId);

    try {
      await bulkApproveMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          participantIds: pendingParticipants.map((p) => p.id),
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${pendingParticipants.length} participant(s) approved`,
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participants",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleApproveAllBoats = async (checkoutId: string) => {
    const checkout = checkouts.find((c) => c.id === checkoutId);
    if (!checkout) return;

    const pendingBoats = (checkout.boats ?? []).filter((boat) =>
      boatIsPending(boat),
    );

    if (pendingBoats.length === 0) return;

    setLoadingCheckoutId(checkoutId);

    try {
      await bulkApproveBoatsMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          boatIds: pendingBoats.map((b) => b.id),
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${pendingBoats.length} boat(s) approved`,
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve boats",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleReject = (participantId: string) => {
    setParticipantIdToReject(participantId);
    setRejectReason("");
    setRejectDialogVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!participantIdToReject || !rejectReason.trim()) return;

    setRejectDialogVisible(false);
    const participantId = participantIdToReject;
    setParticipantIdToReject(null);

    try {
      await rejectMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: { reason: rejectReason.trim() },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant rejected",
      });

      setRejectReason("");
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to reject participant",
      });
    }
  };

  const handleCancel = (participantId: string) => {
    setParticipantIdToCancel(participantId);
    setCancelReason("");
    setCancelRefund(false);
    setCancelDialogVisible(true);
  };

  const handleCancelConfirm = async () => {
    if (!participantIdToCancel || !cancelReason.trim()) return;

    setCancelDialogVisible(false);
    const participantId = participantIdToCancel;
    const checkout = checkouts.find((c) =>
      c.participants?.some((p) => p.id === participantId),
    );
    const participant = checkout?.participants?.find(
      (p) => p.id === participantId,
    );
    setParticipantIdToCancel(null);

    try {
      const hasPaymentCaptured = participant
        ? hasApprovedPaymentCaptured(participant)
        : false;

      const requestBody: { reason: string; refund?: boolean } = {
        reason: cancelReason.trim(),
      };
      if (hasPaymentCaptured) {
        requestBody.refund = cancelRefund;
      }

      await cancelMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: requestBody,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant cancelled",
      });

      setCancelReason("");
      setCancelRefund(false);
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to cancel participant",
      });
    }
  };

  const handleRefund = (checkoutId: string) => {
    setCheckoutIdToRefund(checkoutId);
    setRefundReason("");
    setRefundDialogVisible(true);
  };

  const handleRefundConfirm = async () => {
    if (!checkoutIdToRefund) return;

    setRefundDialogVisible(false);
    const checkoutId = checkoutIdToRefund;
    setCheckoutIdToRefund(null);

    try {
      await refundMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          reason: refundReason.trim() || undefined,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Booking refunded",
      });

      setRefundReason("");
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to refund booking",
      });
    }
  };

  const toggleParticipantSelection = (
    checkoutId: string,
    participantId: string,
  ) => {
    setSelectedParticipants((prev) => {
      const current = prev[checkoutId] ?? [];
      const isSelected = current.includes(participantId);
      return {
        ...prev,
        [checkoutId]: isSelected
          ? current.filter((id) => id !== participantId)
          : [...current, participantId],
      };
    });
  };

  const toggleAllParticipants = (checkoutId: string) => {
    const checkout = checkouts.find((c) => c.id === checkoutId);
    if (!checkout) return;

    const pendingParticipants = (checkout.participants ?? []).filter(
      (p) => p.status === "PENDING" && !p.boatId,
    );

    const current = selectedParticipants[checkoutId] ?? [];
    const allSelected =
      pendingParticipants.length > 0 &&
      pendingParticipants.every((p) => current.includes(p.id));

    setSelectedParticipants((prev) => {
      return {
        ...prev,
        [checkoutId]: allSelected
          ? []
          : pendingParticipants.map((p) => p.id),
      };
    });
  };

  const toggleBoatSelection = (
    checkoutId: string,
    boatId: string,
  ) => {
    setSelectedBoats((prev) => {
      const current = prev[checkoutId] ?? [];
      const isSelected = current.includes(boatId);
      return {
        ...prev,
        [checkoutId]: isSelected
          ? current.filter((id) => id !== boatId)
          : [...current, boatId],
      };
    });
  };

  const toggleAllBoats = (checkoutId: string) => {
    const checkout = checkouts.find((c) => c.id === checkoutId);
    if (!checkout) return;

    const pendingBoats = (checkout.boats ?? []).filter((boat) =>
      boatIsPending(boat),
    );

    const current = selectedBoats[checkoutId] ?? [];
    const allSelected =
      pendingBoats.length > 0 &&
      pendingBoats.every((b) => current.includes(b.id));

    setSelectedBoats((prev) => {
      return {
        ...prev,
        [checkoutId]: allSelected
          ? []
          : pendingBoats.map((b) => b.id),
      };
    });
  };

  const bookingReferenceBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return rowData.bookingReference || "-";
  };

  const statusBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return <CheckoutStatusBadge status={rowData.calculatedStatus} />;
  };

  const totalAmountBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const amount = rowData.totalCents ?? 0;
    if (amount === 0) return "Free";
    const currency = rowData.currency ?? "gbp";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const participantsCountBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const participants = rowData.participants ?? [];
    const pending = participants.filter((p) => p.status === "PENDING").length;
    const approved = participants.filter((p) => p.status === "APPROVED").length;
    return (
      <div className="flex flex-column gap-1">
        <span>
          {pending > 0 && (
            <span className="text-orange-600 font-semibold">{pending} pending</span>
          )}
        </span>
        <span>
          {approved > 0 && (
            <span className="text-green-600">{approved} approved</span>
          )}
        </span>
        <span className="text-gray-600">
          {participants.length} total
        </span>
      </div>
    );
  };

  const actionsBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const participants = rowData.participants ?? [];
    const hasApprovedWithPayment = participants.some((p) =>
      hasApprovedPaymentCaptured(p),
    );

    return (
      <div className="flex gap-2 flex-wrap justify-content-center">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void navigate(`/checkouts/${rowData.id}`);
          }}
          aria-label="View Booking"
        />
        {hasApprovedWithPayment && (
          <Button
            label="Refund"
            icon="pi pi-money-bill"
            className="p-button-warning p-button-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRefund(rowData.id);
            }}
            aria-label="Refund Booking"
          />
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (rowData: AdminCheckoutListItem) => {
    const participants = rowData.participants ?? [];
    const eventParticipants = participants.filter((p) => !p.boatId);
    const pendingParticipants = eventParticipants.filter(
      (p) => p.status === "PENDING",
    );
    const selected = selectedParticipants[rowData.id] ?? [];
    const allSelected =
      pendingParticipants.length > 0 &&
      pendingParticipants.every((p) => selected.includes(p.id));

    const boats = rowData.boats ?? [];
    const pendingBoats = boats.filter((boat) => boatIsPending(boat));
    const selectedBoatsList = selectedBoats[rowData.id] ?? [];
    const allBoatsSelected = pendingBoats.length > 0 && pendingBoats.every(b => selectedBoatsList.includes(b.id));

    const boatRowExpansionTemplate = (boat: (typeof boats)[number]) => {
      const crewParticipants = findCrewForBoat(participants, boat.id);
      return (
        <div className="p-3 bg-gray-50 border-round border-1 border-300">
          <h4 className="mt-0 mb-3">Crew Members</h4>
          <DataTable value={crewParticipants} dataKey="id" className="text-sm">
            <Column
              field="name"
              header="Name"
              body={(p: Participant) => `${p.firstName} ${p.lastName}`}
            />
            <Column
              header="Status"
              body={(p: Participant) => <StatusBadge status={p.status ?? undefined} />}
            />
            <Column
              header="Actions"
              body={(p: Participant) => (
                <Button
                  icon="pi pi-eye"
                  className="p-button-text p-button-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigate(`/participants/${p.id}`);
                  }}
                  aria-label="View"
                  title={`View ${p.firstName} ${p.lastName}`}
                />
              )}
            />
          </DataTable>
        </div>
      );
    };

    return (
      <div className="p-3 flex flex-column gap-4">
        {eventParticipants.length > 0 && (
          <div>
            <div className="flex justify-content-between align-items-center mb-3">
              <h3 className="text-lg font-semibold m-0">Participants</h3>
              <div className="flex gap-2">
                {pendingParticipants.length > 0 && (
                  <>
                    <Button
                      label="Approve Selected"
                      icon="pi pi-check"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleBulkApprove(rowData.id)}
                      disabled={
                        selected.length === 0 || loadingCheckoutId === rowData.id
                      }
                      loading={loadingCheckoutId === rowData.id}
                    />
                    <Button
                      label="Approve All"
                      icon="pi pi-check-circle"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleApproveAll(rowData.id)}
                      disabled={loadingCheckoutId === rowData.id}
                      loading={loadingCheckoutId === rowData.id}
                    />
                  </>
                )}
              </div>
            </div>

            <DataTable
              key={`participants-${rowData.id}-${selected.length}`}
              value={eventParticipants}
              dataKey="id"
              className="text-sm"
              emptyMessage="No participants"
            >
              <Column
                header={
                  pendingParticipants.length > 0 ? (
                    <Checkbox
                      checked={allSelected}
                      onChange={() => toggleAllParticipants(rowData.id)}
                    />
                  ) : null
                }
                body={(participant: Participant) => {
                  if (participant.status !== "PENDING") return null;
                  const isChecked = selected.includes(participant.id);
                  return (
                    <Checkbox
                      inputId={`participant-checkbox-${rowData.id}-${participant.id}`}
                      checked={isChecked}
                      onChange={(e) => {
                        e.originalEvent?.stopPropagation();
                        toggleParticipantSelection(rowData.id, participant.id);
                      }}
                    />
                  );
                }}
                style={{ width: "3rem" }}
              />
              <Column
                field="name"
                header="Name"
                body={(participant: Participant) =>
                  `${participant.firstName} ${participant.lastName}`
                }
              />
              <Column
                header="Status"
                body={(participant: Participant) => (
                  <StatusBadge status={participant.status ?? undefined} />
                )}
              />
              <Column
                header="Event"
                body={(participant: Participant) => {
                  const event = participant.event;
                  if (!event) return "Unknown";
                  const eventTitle = event.title || "Unknown";
                  const startDate = event.startsAt
                    ? formatDateTime(event.startsAt)
                    : null;
                  return (
                    <div className="flex flex-column gap-1">
                      <span>{eventTitle}</span>
                      {startDate && (
                        <span className="text-sm text-gray-600">{startDate}</span>
                      )}
                    </div>
                  );
                }}
              />
              <Column
                header="Amount"
                body={(participant: Participant) => {
                  const amount = participant.paymentAmountCents ?? 0;
                  const formatted = (amount / 100).toFixed(2);
                  const priceLabel = getPricePointLabel(rowData, participant);
                  const amountText = amount === 0 ? "Free" : `£${formatted}`;
                  return (
                    <div className="flex flex-column gap-1">
                      <span>{amountText}</span>
                      {priceLabel && (
                        <span className="text-sm text-gray-600">{priceLabel}</span>
                      )}
                    </div>
                  );
                }}
              />
              <Column
                header="Actions"
                headerStyle={{ textAlign: "center" }}
                bodyStyle={{ textAlign: "center" }}
                body={(participant: Participant) => {
                  const menuId = `menu-${rowData.id}-${participant.id}`;
                  
                  const menuItems: MenuItem[] = [];
                  
                  if (!participant.boatId) {
                    if (participant.status === "PENDING") {
                      menuItems.push({
                        label: "Approve",
                        icon: "pi pi-check",
                        command: () => {
                          void handleApprove(participant.id, rowData.id);
                        },
                        disabled: approvingParticipantId === participant.id || !!approvingParticipantId,
                      });
                      menuItems.push({
                        label: "Reject",
                        icon: "pi pi-times",
                        command: () => {
                          handleReject(participant.id);
                        },
                      });
                      menuItems.push({
                        label: "Cancel",
                        icon: "pi pi-ban",
                        command: () => {
                          handleCancel(participant.id);
                        },
                      });
                    } else if (participant.status === "APPROVED") {
                      menuItems.push({
                        label: "Cancel",
                        icon: "pi pi-ban",
                        command: () => {
                          handleCancel(participant.id);
                        },
                      });
                    }
                  }

                  return (
                    <div className="flex align-items-center justify-content-center gap-2">
                      <Button
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          void navigate(`/participants/${participant.id}`);
                        }}
                        aria-label="View"
                      />
                      {menuItems.length > 0 && (
                        <>
                          <Menu
                            ref={(el) => {
                              menuRefs.current[menuId] = el;
                            }}
                            model={menuItems}
                            popup
                            id={menuId}
                          />
                          <Button
                            icon="pi pi-ellipsis-v"
                            className="p-button-text p-button-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              const ref = menuRefs.current[menuId];
                              if (ref) {
                                ref.toggle(e);
                              }
                            }}
                            aria-label="Actions"
                            aria-controls={menuId}
                            aria-haspopup
                          />
                        </>
                      )}
                    </div>
                  );
                }}
              />
            </DataTable>
          </div>
        )}

        {boats.length > 0 && (
          <div>
            <div className="flex justify-content-between align-items-center mb-3">
              <h3 className="text-lg font-semibold m-0">Boats</h3>
              <div className="flex gap-2">
                {pendingBoats.length > 0 && (
                  <>
                    <Button
                      label="Approve Selected"
                      icon="pi pi-check"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleBulkApproveBoats(rowData.id)}
                      disabled={
                        selectedBoatsList.length === 0 || loadingCheckoutId === rowData.id
                      }
                      loading={loadingCheckoutId === rowData.id}
                    />
                    <Button
                      label="Approve All"
                      icon="pi pi-check-circle"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleApproveAllBoats(rowData.id)}
                      disabled={loadingCheckoutId === rowData.id}
                      loading={loadingCheckoutId === rowData.id}
                    />
                  </>
                )}
              </div>
            </div>

            <DataTable
              key={`boats-${rowData.id}-${selectedBoatsList.length}`}
              value={boats}
              dataKey="id"
              className="text-sm"
              emptyMessage="No boats"
              expandedRows={expandedBoatRows || {}}
              onRowToggle={(e) => setExpandedBoatRows(e.data as Record<string, boolean>)}
              rowExpansionTemplate={boatRowExpansionTemplate}
            >
              <Column expander style={{ width: "3rem" }} />
              <Column
                header={
                  pendingBoats.length > 0 ? (
                    <Checkbox
                      checked={allBoatsSelected}
                      onChange={() => toggleAllBoats(rowData.id)}
                    />
                  ) : null
                }
                body={(boat: (typeof boats)[number]) => {
                  const isPending = boatIsPending(boat);
                  if (!isPending) return null;
                  const isChecked = selectedBoatsList.includes(boat.id);
                  return (
                    <Checkbox
                      inputId={`boat-checkbox-${rowData.id}-${boat.id}`}
                      checked={isChecked}
                      onChange={(e) => {
                        e.originalEvent?.stopPropagation();
                        toggleBoatSelection(rowData.id, boat.id);
                      }}
                    />
                  );
                }}
                style={{ width: "3rem" }}
              />
              <Column
                header="Event"
                body={(boat: (typeof boats)[number]) => {
                  const event = boat.event;
                  if (!event) return "Unknown";
                  const eventTitle = event.title || "Unknown";
                  const startDate = event.startsAt
                    ? formatDateTime(event.startsAt)
                    : null;
                  return (
                    <div className="flex flex-column gap-1">
                      <span>{eventTitle}</span>
                      {startDate && (
                        <span className="text-xs text-500">{startDate}</span>
                      )}
                    </div>
                  );
                }}
              />
              <Column
                header="Boat"
                body={(boat: (typeof boats)[number]) =>
                  `${boat.class} - ${boat.sailNumber}`
                }
              />
              <Column
                header="Status"
                body={(boat: (typeof boats)[number]) => (
                  <StatusBadge status={boat.status ?? undefined} />
                )}
              />
              <Column
                header="Price point"
                body={(boat: (typeof boats)[number]) => {
                  const pricePoint = boat.event?.pricePoints?.find(
                    (pp) => pp.id === boat.eventPricePointId,
                  );
                  return pricePoint?.label ?? "—";
                }}
              />
              <Column
                header="Amount"
                body={(boat: (typeof boats)[number]) => {
                  const amount = boat.paymentAmountCents ?? 0;
                  const formatted = (amount / 100).toFixed(2);
                  return amount === 0 ? "Free" : `£${formatted}`;
                }}
              />
            </DataTable>
          </div>
        )}
      </div>
    );
  };

  const dateBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return rowData.createdAt ? formatDateTime(rowData.createdAt) : "-";
  };

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Pending Bookings</h1>
        <span className="p-input-icon-left w-full md:w-auto">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={onFilter}
            placeholder="Search by booking reference..."
            className="w-full"
          />
        </span>
      </div>

      <DataTable
        value={checkouts}
        dataKey="id"
        lazy
        paginator
        rows={lazyParams.rows}
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No pending bookings"
        className="text-sm"
        expandedRows={expandedRows ?? {}}
        onRowToggle={(e) => {
          const newExpandedRows = e.data;
          if (newExpandedRows && typeof newExpandedRows === "object") {
            setExpandedRows(newExpandedRows as Record<string, boolean>);
          } else {
            setExpandedRows({});
          }
        }}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          field="bookingReference"
          header="Booking Reference"
          body={bookingReferenceBodyTemplate}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          field="totalCents"
          header="Total Amount"
          body={totalAmountBodyTemplate}
          sortable
          style={{ minWidth: "120px" }}
        />
        <Column
          header="Participants"
          body={participantsCountBodyTemplate}
          style={{ minWidth: "150px" }}
        />
        <Column
          field="createdAt"
          header="Created"
          body={dateBodyTemplate}
          sortable
          style={{ minWidth: "160px" }}
        />
        <Column
          body={actionsBodyTemplate}
          header="Actions"
          style={{ minWidth: "150px" }}
        />
      </DataTable>

      {/* Reject Dialog */}
      <Dialog
        header="Reject Participant"
        visible={rejectDialogVisible}
        style={{ width: "500px" }}
        onHide={() => {
          setRejectDialogVisible(false);
          setRejectReason("");
          setParticipantIdToReject(null);
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setRejectDialogVisible(false);
                setRejectReason("");
                setParticipantIdToReject(null);
              }}
            />
            <Button
              label="Reject"
              icon="pi pi-times"
              className="p-button-danger"
              onClick={() => {
                void handleRejectConfirm();
              }}
              disabled={!rejectReason.trim()}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField id="reject-reason" label="Reason for Rejection" required>
            <InputTextarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for rejecting this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>
        </div>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        header="Cancel Participant"
        visible={cancelDialogVisible}
        style={{ width: "500px" }}
        onHide={() => {
          setCancelDialogVisible(false);
          setCancelReason("");
          setCancelRefund(false);
          setParticipantIdToCancel(null);
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setCancelDialogVisible(false);
                setCancelReason("");
                setCancelRefund(false);
                setParticipantIdToCancel(null);
              }}
              disabled={cancelMutation.isPending}
            />
            <Button
              label="Cancel Participant"
              icon="pi pi-ban"
              className="p-button-warning"
              onClick={() => {
                void handleCancelConfirm();
              }}
              loading={cancelMutation.isPending}
              disabled={!cancelReason.trim() || cancelMutation.isPending}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField
            id="cancel-reason"
            label="Reason for Cancellation"
            required
          >
            <InputTextarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for cancelling this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>

          {participantIdToCancel && (() => {
            const checkout = checkouts.find((c) =>
              c.participants?.some((p) => p.id === participantIdToCancel),
            );
            const participant = checkout?.participants?.find(
              (p) => p.id === participantIdToCancel,
            );
            if (!participant || !hasApprovedPaymentCaptured(participant)) {
              return null;
            }

            const hasBalancePayment = hasApprovedBalancePayment(participant);
            const hasDepositPayment = hasApprovedDepositPayment(participant);

            return (
              <div
                className="flex align-items-center gap-2"
                style={{ marginTop: "1rem" }}
              >
                <Checkbox
                  inputId="cancel-refund"
                  checked={cancelRefund}
                  onChange={(e) => setCancelRefund(e.checked ?? false)}
                />
                <label htmlFor="cancel-refund" className="cursor-pointer">
                  Issue refund
                  {hasDepositPayment && hasBalancePayment
                    ? " (deposit and balance)"
                    : hasDepositPayment
                      ? " (deposit)"
                      : " (balance)"}
                </label>
              </div>
            );
          })()}
        </div>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        header="Refund Booking"
        visible={refundDialogVisible}
        style={{ width: "600px" }}
        onHide={() => {
          setRefundDialogVisible(false);
          setRefundReason("");
          setCheckoutIdToRefund(null);
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setRefundDialogVisible(false);
                setRefundReason("");
                setCheckoutIdToRefund(null);
              }}
            />
            <Button
              label="Refund"
              icon="pi pi-money-bill"
              className="p-button-warning"
              onClick={() => {
                void handleRefundConfirm();
              }}
              loading={refundMutation.isPending}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          {checkoutIdToRefund && (
            <>
              <div className="mb-3">
                <p className="font-semibold mb-2">This will refund all approved participants in this booking.</p>
                <p className="text-sm text-gray-600">
                  All participants will be cancelled and their payments refunded.
                </p>
              </div>
              <FormField id="refund-reason" label="Reason (optional)">
                <InputTextarea
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={5}
                  placeholder="Optional reason for the refund..."
                  className="w-full"
                  style={formStyles.input}
                />
              </FormField>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}

